/**
 * EventTimeline Component
 * Displays paginated event log with filtering and real-time updates
 * Redesigned for clarity and non-technical accessibility
 */

import React, { useState, useMemo } from 'react';
import { useDashboardStore } from '../store/dashboardStore';
import { ChevronLeft, ChevronRight, Activity } from 'lucide-react';

const EVENT_CONFIG = {
  cache_hit: {
    icon: '⚡',
    label: 'Cache Hit',
    description: 'Answer served from memory',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500',
    badgeBg: 'bg-emerald-100',
    badgeText: 'text-emerald-700',
  },
  cache_miss: {
    icon: '🔄',
    label: 'New Request',
    description: 'Sent to AI for fresh answer',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
    dot: 'bg-blue-500',
    badgeBg: 'bg-blue-100',
    badgeText: 'text-blue-700',
  },
  agent_join: {
    icon: '🟢',
    label: 'Agent Connected',
    description: 'A new agent joined the network',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    text: 'text-indigo-700',
    dot: 'bg-indigo-500',
    badgeBg: 'bg-indigo-100',
    badgeText: 'text-indigo-700',
  },
  agent_leave: {
    icon: '🔴',
    label: 'Agent Disconnected',
    description: 'An agent left the network',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    dot: 'bg-amber-400',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-700',
  },
  error: {
    icon: '⚠️',
    label: 'Error',
    description: 'Something went wrong',
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    dot: 'bg-red-500',
    badgeBg: 'bg-red-100',
    badgeText: 'text-red-700',
  },
  mcp_tool_call: {
    icon: '🛠️',
    label: 'MCP Tool Called',
    description: 'Tool execution',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
    dot: 'bg-purple-500',
    badgeBg: 'bg-purple-100',
    badgeText: 'text-purple-700',
  },
  brain_hit: {
    icon: '🧠',
    label: 'Brain Cache Hit',
    description: 'Memory recalled successfully',
    bg: 'bg-cyan-50',
    border: 'border-cyan-200',
    text: 'text-cyan-700',
    dot: 'bg-cyan-500',
    badgeBg: 'bg-cyan-100',
    badgeText: 'text-cyan-700',
  },
  brain_miss: {
    icon: '🔍',
    label: 'Brain Cache Miss',
    description: 'No matching memory found',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    text: 'text-orange-700',
    dot: 'bg-orange-500',
    badgeBg: 'bg-orange-100',
    badgeText: 'text-orange-700',
  },
  contextSqueeze: {
    icon: '🗜️',
    label: 'Context Squeezed',
    description: 'Tokens optimized',
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    text: 'text-teal-700',
    dot: 'bg-teal-500',
    badgeBg: 'bg-teal-100',
    badgeText: 'text-teal-700',
  },
  complexityRouting: {
    icon: '🔀',
    label: 'Model Routed',
    description: 'Model selected for complexity',
    bg: 'bg-pink-50',
    border: 'border-pink-200',
    text: 'text-pink-700',
    dot: 'bg-pink-500',
    badgeBg: 'bg-pink-100',
    badgeText: 'text-pink-700',
  },
  historyPrune: {
    icon: '📜',
    label: 'History Pruned',
    description: 'Old memory removed',
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-700',
    dot: 'bg-yellow-500',
    badgeBg: 'bg-yellow-100',
    badgeText: 'text-yellow-700',
  },
  clipboard: {
    icon: '📋',
    label: 'Clipboard Captured',
    description: 'Clipboard content processed',
    bg: 'bg-lime-50',
    border: 'border-lime-200',
    text: 'text-lime-700',
    dot: 'bg-lime-500',
    badgeBg: 'bg-lime-100',
    badgeText: 'text-lime-700',
  },
  default: {
    icon: '●',
    label: 'Event',
    description: 'System event',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-700',
    dot: 'bg-gray-400',
    badgeBg: 'bg-gray-100',
    badgeText: 'text-gray-600',
  },
};

const FILTER_OPTIONS = [
  { value: 'all', label: 'All Events', emoji: '📋' },
  { value: 'cache_hit', label: 'Cache Hits', emoji: '⚡' },
  { value: 'cache_miss', label: 'New Requests', emoji: '🔄' },
  { value: 'agent_join', label: 'Connections', emoji: '🟢' },
  { value: 'agent_leave', label: 'Disconnections', emoji: '🔴' },
  { value: 'error', label: 'Errors', emoji: '⚠️' },
  { value: 'mcp_tool_call', label: 'MCP Tools', emoji: '🛠️' },
  { value: 'brain_hit', label: 'Brain Hits', emoji: '🧠' },
  { value: 'brain_miss', label: 'Brain Misses', emoji: '🔍' },
  { value: 'contextSqueeze', label: 'Squeezed', emoji: '🗜️' },
  { value: 'complexityRouting', label: 'Routed', emoji: '🔀' },
  { value: 'historyPrune', label: 'Pruned', emoji: '📜' },
  { value: 'clipboard', label: 'Clipboard', emoji: '📋' },
];

export const EventTimeline: React.FC = () => {
  const events = useDashboardStore((state) => state.events);
  const [filterType, setFilterType] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 15;

  const filteredEvents = useMemo(() => {
    if (filterType === 'all') return events;
    return events.filter((event) => event.type === filterType);
  }, [events, filterType]);

  const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);
  const paginatedEvents = useMemo(() => {
    const startIdx = (currentPage - 1) * eventsPerPage;
    return filteredEvents.slice(startIdx, startIdx + eventsPerPage);
  }, [filteredEvents, currentPage]);

  const handlePreviousPage = () => setCurrentPage((prev) => Math.max(1, prev - 1));
  const handleNextPage = () => setCurrentPage((prev) => Math.min(totalPages, prev + 1));

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  // Quick stats
  const hitCount = events.filter((e) => e.type === 'cache_hit').length;
  const missCount = events.filter((e) => e.type === 'cache_miss').length;
  const errorCount = events.filter((e) => e.type === 'error').length;

  return (
    <div className="flex flex-col h-full bg-transparent rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/5 bg-white/5 flex-shrink-0">
        {/* Title Row */}
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
            <Activity className="w-3.5 h-3.5 text-white" />
          </div>
          <h2 className="text-[10px] font-black text-white uppercase tracking-widest">Live Activity Feed</h2>
          <span className="ml-auto text-[10px] font-black px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full border border-blue-500/30">
            {filteredEvents.length}
          </span>
        </div>

        {/* Filter Chips + Stats */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {events.length > 0 && (
            <>
              <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                ⚡ {hitCount}
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
                🔄 {missCount}
              </span>
              {errorCount > 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20">
                  ⚠️ {errorCount}
                </span>
              )}
              <span className="w-px h-3 bg-white/5 mx-0.5 flex-shrink-0" />
            </>
          )}
          {FILTER_OPTIONS.map((opt) => {
            const count = opt.value === 'all'
              ? events.length
              : events.filter((e) => e.type === opt.value).length;

            if (opt.value !== 'all' && count === 0) return null;

            return (
              <button
                key={opt.value}
                onClick={() => { setFilterType(opt.value); setCurrentPage(1); }}
                className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-md border transition-all duration-150 ${
                  filterType === opt.value
                    ? 'bg-indigo-600/20 text-indigo-400 border-indigo-500/50 shadow-[0_0_15px_rgba(79,70,229,0.2)]'
                    : 'bg-white/5 text-slate-500 border-white/5 hover:border-white/10 hover:bg-white/10 hover:text-slate-300'
                }`}
              >
                {opt.emoji} {opt.label}
                {count > 0 && (
                  <span className={`ml-0.5 text-xs ${filterType === opt.value ? 'text-indigo-200' : 'text-gray-400'}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Events List */}
      <div className="flex-1 overflow-y-auto">
        {paginatedEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full px-6 text-center py-12">
            <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center mb-3 border border-white/5">
              <Activity className="w-6 h-6 text-slate-700" />
            </div>
            <p className="text-white font-bold text-sm">
              {events.length === 0 ? 'No activity yet' : 'No events match this filter'}
            </p>
            <p className="text-slate-500 text-[10px] uppercase font-bold tracking-widest mt-2 max-w-[180px]">
              {events.length === 0
                ? 'Events will appear here as agents process requests.'
                : 'Try selecting a different filter above.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {paginatedEvents.map((event) => {
              const config =
                EVENT_CONFIG[event.type as keyof typeof EVENT_CONFIG] ||
                EVENT_CONFIG.default;

              return (
                <div
                  key={event.id}
                  className="px-4 py-3 hover:bg-white/5 transition-colors duration-100 border-b border-white/5 last:border-b-0"
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-sm bg-black/20 border border-white/5`}>
                      {config.icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className={`text-[10px] font-black uppercase tracking-widest text-white`}>
                            {config.label}
                          </span>
                          {event.latency && (
                            <span className="text-[10px] text-slate-500 font-mono font-bold">{event.latency}ms</span>
                          )}
                        </div>
                        <span className="text-[10px] text-slate-500 font-bold flex-shrink-0 tabular-nums">
                          {formatTime(event.timestamp)}
                        </span>
                      </div>

                      <p className="text-[10px] text-slate-500 mt-1 font-medium">{config.description}</p>

                      {/* Supplemental data pills */}
                      {(event.tokensSaved || event.costSaved) && (
                        <div className="flex gap-1.5 mt-2">
                          {event.tokensSaved && (
                            <span className="text-[10px] font-black uppercase tracking-tighter bg-violet-500/10 text-violet-400 px-1.5 py-0.5 rounded border border-violet-500/20">
                              {event.tokensSaved} tokens saved
                            </span>
                          )}
                          {event.costSaved && (
                            <span className="text-[10px] font-black uppercase tracking-tighter bg-emerald-500/10 text-emerald-400 px-1.5 py-0.5 rounded border border-emerald-500/20">
                              ${event.costSaved.toFixed(3)} saved
                            </span>
                          )}
                        </div>
                      )}

                      {event.agentId && (
                        <p className="text-xs text-gray-400 mt-0.5 font-mono">
                          Agent: {event.agentId.substring(0, 12)}…
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination Footer */}
      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-white/5 bg-black/20 flex items-center justify-between flex-shrink-0">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className="p-1.5 hover:bg-white/5 disabled:opacity-20 disabled:cursor-not-allowed rounded-lg border border-white/5 transition"
          >
            <ChevronLeft className="w-4 h-4 text-slate-400" />
          </button>

          <span className="text-[10px] text-slate-500 font-black uppercase tracking-widest">
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="p-1.5 hover:bg-white/5 disabled:opacity-20 disabled:cursor-not-allowed rounded-lg border border-white/5 transition"
          >
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      )}
    </div>
  );
};
